/**
 * Central registry: URL pattern → required feature and/or permission.
 *
 * The {@link accessInterceptor} consults this list on every outgoing HTTP
 * request. If a matching entry declares requirements the client already knows
 * are not met (permission missing OR feature disabled), the request is
 * short-circuited with a synthetic 403 — the network round-trip is skipped
 * entirely.
 *
 * This is a UX optimisation, NOT a security boundary. The server-side
 * FeatureGuard + RBAC guards remain the authoritative enforcement.
 *
 * Rules for adding entries:
 * - `pattern` matches the request URL AFTER the tenant host (e.g. '/api/v1/plans').
 * - Order matters — first match wins. Put specific patterns before generic ones.
 * - Include BOTH `feature` and `permission` when both are relevant; the AND is
 *   applied by the interceptor (both must be satisfied to allow the request).
 * - Use `featureAnyOf` when ANY one of several features grants access (OR);
 *   it is mutually exclusive with `feature`.
 * - Prefer FeatureKey (fine-grained) for `feature`; ModuleKey (coarse) also
 *   works — {@link FeatureAccessClientService.hasFeature} handles both.
 * - Foundation endpoints (auth, /api/v1/users/me, /api/v1/features,
 *   /api/v1/audit, /api/v1/settings, /api/v1/rbac, /api/v1/tenants,
 *   /api/v1/environments) should NOT be in this registry — they must always
 *   fire so the app can bootstrap.
 * - `methods` optionally overrides `permission` per HTTP method (e.g. a
 *   read permission for GET and a manage permission for mutating verbs).
 *   When the request method has no override, the entry-level `permission`
 *   applies as the fallback.
 */
export type AccessRegistryMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS';

export interface AccessRegistryEntry {
  readonly pattern: RegExp;
  readonly feature?: string;
  readonly featureAnyOf?: readonly string[];
  readonly permission?: string;
  readonly methods?: Readonly<Partial<Record<AccessRegistryMethod, string>>>;
}

export const ACCESS_REGISTRY: readonly AccessRegistryEntry[] = [
  // ─── E2E feature fixtures ─────────────────────────────────────────────────
  { pattern: /^\/api\/v1\/testing\/features\/gated\b/, feature: 'test.gated-endpoint' },
  { pattern: /^\/api\/v1\/testing\/features\/flag-only\b/, feature: 'test.flag-only-endpoint' },

  // ─── Timetracking ─────────────────────────────────────────────────────────
  {
    pattern: /^\/api\/v1\/time-tracking\/my-week\/(?:time|convert|entries)\b/,
    feature: 'timetracking.clock-in',
    permission: 'timetracking:my-week:read',
  },
  { pattern: /^\/api\/v1\/time-tracking\/my-week\b/, feature: 'timetracking.balances', permission: 'timetracking:my-week:read' },
  { pattern: /^\/api\/v1\/time-tracking\/review-queue\b/, feature: 'timetracking.reports', permission: 'timetracking:review-queue:read' },
  { pattern: /^\/api\/v1\/time-tracking\/absences\b/, feature: 'timetracking.balances', permission: 'timetracking:absences:read' },
  { pattern: /^\/api\/v1\/time-tracking\/clock-(?:state|in)\b/, feature: 'timetracking.clock-in' },
  { pattern: /^\/api\/v1\/time-tracking\/clock-out\b/, feature: 'timetracking.clock-out' },
  { pattern: /^\/api\/v1\/core\/persons\/[^/]+\/time-tracking\/manual-bookings\b/, feature: 'timetracking.clock-in' },
  { pattern: /^\/api\/v1\/time-tracking\/rule-bundles\b/, feature: 'timetracking.settings', permission: 'timetracking:rule-bundles:read' },
  { pattern: /^\/api\/v1\/time-tracking\/periods\b/, feature: 'timetracking.settings', permission: 'timetracking:periods:read' },
  { pattern: /^\/api\/v1\/core\/persons\/[^/]+\/time-tracking\/entitlements\b/, feature: 'timetracking.balances' },
  { pattern: /^\/api\/v1\/time-tracking\b/, feature: 'timetracking' },
  { pattern: /^\/api\/v1\/core\/persons\/[^/]+\/time-tracking\b/, feature: 'timetracking' },

  // ─── Terminals ────────────────────────────────────────────────────────────
  { pattern: /^\/api\/v1\/terminals\b/, feature: 'terminals.devices' },
  { pattern: /^\/api\/v1\/core\/persons\/[^/]+\/terminal-(badges|wiring)\b/, feature: 'terminals.devices' },

  // ─── Planning ─────────────────────────────────────────────────────────────
  { pattern: /^\/api\/v1\/plans\b/, feature: 'planning.plans', permission: 'plans:read' },
  { pattern: /^\/api\/v1\/plan-instances\b/, feature: 'planning.plans', permission: 'plan-instances:read' },
  { pattern: /^\/api\/v1\/shifts\b/, feature: 'planning.shifts.manage', permission: 'shifts:read' },
  { pattern: /^\/api\/v1\/shift-units\b/, feature: 'planning.shifts.manage', permission: 'shift-units:read' },
  { pattern: /^\/api\/v1\/shift-applications\b/, feature: 'planning.assignments', permission: 'shift-applications:read' },
  { pattern: /^\/api\/v1\/shift-swaps\b/, feature: 'planning.assignments', permission: 'shift-swaps:read' },
  { pattern: /^\/api\/v1\/labour-pool\b/, feature: 'planning.assignments', permission: 'labour-pool:read' },
  { pattern: /^\/api\/v1\/requests\b/, feature: 'planning.assignments', permission: 'requests:read' },
  { pattern: /^\/api\/v1\/strategies\b/, feature: 'planning.rule-engine', permission: 'strategies:read' },
  { pattern: /^\/api\/v1\/skills\b/, feature: 'employees.view', permission: 'skills:read' },
  { pattern: /^\/api\/v1\/planning\b/, feature: 'planning' },
  { pattern: /^\/api\/v1\/core\/skills\b/, feature: 'employees' },
  { pattern: /^\/api\/v1\/core\/persons\/[^/]+\/skills\b/, feature: 'planning' },
  { pattern: /^\/api\/v1\/dashboard\b/, feature: 'planning' },
  { pattern: /^\/api\/v1\/users\/me\/(?:shift-calendar|available-shifts|shift-applications)\b/, feature: 'planning' },

  // ─── Documents ────────────────────────────────────────────────────────────
  { pattern: /^\/api\/v1\/documents\/templates\b/, feature: 'documents.templates', permission: 'templates:read' },
  { pattern: /^\/api\/v1\/documents\/generate\b/, feature: 'documents.generate', permission: 'documents:generate' },
  { pattern: /^\/api\/v1\/documents\b/, feature: 'documents.templates', permission: 'documents:read' },
  { pattern: /^\/api\/v1\/personal-documents\b/, feature: 'documents.templates', permission: 'documents:read:own' },
  { pattern: /^\/api\/v1\/templates\b/, featureAnyOf: ['documents.templates', 'email.templates'] },
  { pattern: /^\/api\/v1\/users\/me\/documents\/personal\b/, feature: 'documents' },

  // ─── Email ────────────────────────────────────────────────────────────────
  { pattern: /^\/api\/v1\/email\/template-configs\b/, feature: 'email.templates', permission: 'email:read' },
  { pattern: /^\/api\/v1\/email\/templates\b/, feature: 'email.templates', permission: 'email:read' },

  // ─── Public API ───────────────────────────────────────────────────────────
  { pattern: /^\/api\/v1\/users\/me\/api-keys\b/, feature: 'public-api.api-keys' },
  {
    pattern: /^\/api\/v1\/api-keys\b/,
    feature: 'public-api.api-keys',
    permission: 'api-keys:read',
    methods: { POST: 'api-keys:manage', PATCH: 'api-keys:manage', DELETE: 'api-keys:manage' },
  },

  // ─── Storage / Files ──────────────────────────────────────────────────────
  { pattern: /^\/api\/v1\/files\b/, feature: 'storage.files', permission: 'files:read' },

  // ─── Employees / Persons ──────────────────────────────────────────────────
  { pattern: /^\/api\/v1\/persons\b/, feature: 'employees.view', permission: 'employees:read' },
  { pattern: /^\/api\/v1\/job-roles\b/, feature: 'employees.manage', permission: 'job-roles:update' },

  // ─── Projects ─────────────────────────────────────────────────────────────
  { pattern: /^\/api\/v1\/projects\/my-time\b/, feature: 'projects.tasks', permission: 'projects:my-time:read' },
  { pattern: /^\/api\/v1\/projects\b/, feature: 'projects.view', permission: 'projects:read' },
  { pattern: /^\/api\/v1\/project-(?:activity-types|billing-settings|cost-rates|invoices|time)\b/, feature: 'projects' },

  // ─── Locations ────────────────────────────────────────────────────────────
  { pattern: /^\/api\/v1\/locations\b/, feature: 'locations.view', permission: 'locations:read' },

  // ─── Calendar ─────────────────────────────────────────────────────────────
  { pattern: /^\/api\/v1\/calendar\/calendars\/[^/]+\/outlook\b/, feature: 'outlook' },
  { pattern: /^\/api\/v1\/calendars\b/, feature: 'calendar.view', permission: 'calendars:read' },
  { pattern: /^\/api\/v1\/calendar\b/, feature: 'calendar.view' },

  // ─── Notifications / Webhooks ─────────────────────────────────────────────
  { pattern: /^\/api\/v1\/notifications\/webhooks\b/, feature: 'public-api.webhooks', permission: 'webhooks:read' },
  { pattern: /^\/api\/v1\/notifications\b/, feature: 'notifications.push', permission: 'notifications:read' },

  // ─── AI ───────────────────────────────────────────────────────────────────
  { pattern: /^\/api\/v1\/ai\b/, feature: 'ai.chat' },

  // ─── Reports ──────────────────────────────────────────────────────────────
  { pattern: /^\/api\/v1\/reports\b/, feature: 'timetracking.reports', permission: 'reports:generate' },

  // ─── Outlook sync ─────────────────────────────────────────────────────────
  { pattern: /^\/api\/v1\/(?:users\/me|webhooks)\/outlook\b/, feature: 'outlook' },
  { pattern: /^\/api\/v1\/outlook\b/, feature: 'outlook.calendar-sync' },

  // ─── Bulk edit ──────────────────────────────────────────────────────────────
  { pattern: /^\/api\/v1\/me\/bulk-edit-jobs\b/, feature: 'bulk-edit.execute', permission: 'bulk-edit:execute' },
  { pattern: /^\/api\/v1\/rbac\/users\/(select-ids|bulk-update)\b/, feature: 'bulk-edit.execute', permission: 'bulk-edit:execute' },
  { pattern: /^\/api\/v1\/rbac\/roles\/(select-ids|bulk-update)\b/, feature: 'bulk-edit.execute', permission: 'bulk-edit:execute' },
  { pattern: /^\/api\/v1\/rbac\/groups\/(select-ids|bulk-update)\b/, feature: 'bulk-edit.execute', permission: 'bulk-edit:execute' },
  { pattern: /^\/api\/v1\/core\/persons\/(select-ids|bulk-update)\b/, feature: 'bulk-edit.execute', permission: 'bulk-edit:execute' },

  // ─── Tenant app controllers ───────────────────────────────────────────────
  { pattern: /^\/api\/v1\/core\/persons\/exports\b/, feature: 'employees.view' },
  { pattern: /^\/api\/v1\/settings\/ai\b/, feature: 'ai.chat' },
];

/**
 * Resolves the registry entry that matches the request URL, or `undefined` if
 * no entry applies. First match wins. When `method` has a per-method
 * permission override in the entry's `methods` map, the returned entry
 * carries the resolved `permission`.
 */
export function matchAccessRegistryEntry(url: string, method?: string): AccessRegistryEntry | undefined {
  const entry = ACCESS_REGISTRY.find(candidate => candidate.pattern.test(url));
  if (!entry) return undefined;
  const override = method !== undefined ? entry.methods?.[method.toUpperCase() as AccessRegistryMethod] : undefined;
  if (override === undefined) return entry;
  return { ...entry, permission: override };
}
